import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableFooter } from '../ui/table';
import { getStartDate } from './CostChart';

const PROVIDER_COLORS = {
  aws: '#FF9900',
  azure: '#007FFF',
  gcp: '#4285F4',
  ovh: '#123F6D'
};

const PROVIDER_NAMES = {
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP',
  ovh: 'OVH'
};

const RANGES = [
  { key: '1m', label: '1 mois' },
  { key: '3m', label: '3 mois' },
  { key: '6m', label: '6 mois' },
  { key: '12m', label: '12 mois' },
  { key: '24m', label: '24 mois' },
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear];

const formatCost = (val) =>
  val.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const computeMonthlyBreakdown = (providers, filter) => {
  let startStr, endStr;

  if (typeof filter === 'number') {
    // Year filter
    startStr = `${filter}-01-01`;
    endStr = `${filter}-12-31`;
  } else {
    // Range filter (1m, 3m, etc.)
    startStr = getStartDate(filter).toISOString().slice(0, 10);
    endStr = null;
  }

  const monthsSet = new Set();
  const providerMonthCosts = {};

  providers.forEach(p => {
    providerMonthCosts[p.provider] = {};
    (p.cost_by_day || []).forEach(d => {
      if (d.date >= startStr && (!endStr || d.date <= endStr)) {
        const month = d.date.slice(0, 7);
        monthsSet.add(month);
        providerMonthCosts[p.provider][month] = (providerMonthCosts[p.provider][month] || 0) + d.cost;
      }
    });
  });

  const months = Array.from(monthsSet).sort();

  const monthLabels = months.map(m => {
    const [y, mo] = m.split('-');
    const d = new Date(parseInt(y), parseInt(mo) - 1, 1);
    const label = d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    return { key: m, label: label.charAt(0).toUpperCase() + label.slice(1) };
  });

  const providerTotals = {};
  providers.forEach(p => {
    providerTotals[p.provider] = Object.values(providerMonthCosts[p.provider]).reduce((s, c) => s + c, 0);
  });
  const sortedProviders = providers
    .map(p => p.provider)
    .filter(p => providerTotals[p] > 0)
    .sort((a, b) => providerTotals[b] - providerTotals[a]);

  const monthTotals = {};
  months.forEach(m => {
    monthTotals[m] = sortedProviders.reduce((s, p) => s + (providerMonthCosts[p]?.[m] || 0), 0);
  });

  return { months: monthLabels, providerMonthCosts, sortedProviders, monthTotals, providerTotals };
};

export const CostTable = ({ data }) => {
  const [filter, setFilter] = useState('6m');
  const currentMonth = new Date().toISOString().slice(0, 7);

  const providers = (data?.providers || []).filter(
    p => p.cost_by_day && p.cost_by_day.length > 0
  );

  const { months, providerMonthCosts, sortedProviders, monthTotals, providerTotals } =
    computeMonthlyBreakdown(providers, filter);

  return (
    <Card className="flex flex-col" data-testid="cost-table">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0 pb-4">
        <CardTitle className="text-lg font-[Outfit]">Détail mensuel</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1 flex-1 sm:flex-initial">
            {RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setFilter(r.key)}
                className={
                  'flex-1 sm:flex-initial px-2 sm:px-3 py-1 text-xs font-medium rounded-md transition-colors ' +
                  (filter === r.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground')
                }
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {YEARS.map(y => (
              <button
                key={y}
                onClick={() => setFilter(y)}
                className={
                  'px-3 py-1 text-xs font-medium rounded-md transition-colors ' +
                  (filter === y
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground')
                }
              >
                {y}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto p-0 sm:p-6">
        {sortedProviders.length === 0 ? (
          <div className="flex items-center justify-center h-[250px]">
            <p className="text-muted-foreground text-sm">Aucune donnée sur cette période.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mois</TableHead>
                {sortedProviders.map(p => (
                  <TableHead key={p} className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: PROVIDER_COLORS[p] }}
                      />
                      {PROVIDER_NAMES[p]}
                    </div>
                  </TableHead>
                ))}
                {sortedProviders.length > 1 && (
                  <TableHead className="text-right font-semibold">Total</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {months.map(m => (
                <TableRow key={m.key} className={m.key === currentMonth ? 'bg-primary/5' : undefined}>
                  <TableCell className="font-medium whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      {m.label}
                      {m.key === currentMonth && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/15 text-primary leading-none">
                          En cours
                        </span>
                      )}
                    </span>
                  </TableCell>
                  {sortedProviders.map(p => (
                    <TableCell key={p} className="text-right tabular-nums">
                      {formatCost(providerMonthCosts[p]?.[m.key] || 0)} €
                    </TableCell>
                  ))}
                  {sortedProviders.length > 1 && (
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatCost(monthTotals[m.key] || 0)} €
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="font-semibold">Total</TableCell>
                {sortedProviders.map(p => (
                  <TableCell key={p} className="text-right tabular-nums font-semibold">
                    {formatCost(providerTotals[p] || 0)} €
                  </TableCell>
                ))}
                {sortedProviders.length > 1 && (
                  <TableCell className="text-right tabular-nums font-semibold">
                    {formatCost(Object.values(monthTotals).reduce((s, c) => s + c, 0))} €
                  </TableCell>
                )}
              </TableRow>
            </TableFooter>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
